import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./OurImpact.css";

/* ---- CountUp ---- */
// Parses strings like "17+", "14,000+", "27", "100s" and animates the
// numeric portion from 0 to its target value, preserving any prefix/suffix
// and comma formatting. Non-numeric strings render unchanged.
function parseStatString(str) {
  const match = String(str).match(/^([^\d]*)([\d,]+)(.*)$/);
  if (!match) return null;
  const [, prefix, numberPart, suffix] = match;
  const target = parseInt(numberPart.replace(/,/g, ""), 10);
  if (isNaN(target)) return null;
  const hasCommas = numberPart.includes(",");
  return { prefix, target, suffix, hasCommas };
}

function formatNumber(value, hasCommas) {
  return hasCommas ? value.toLocaleString("en-US") : String(value);
}

function CountUp({ value, duration = 1500 }) {
  const parsed = parseStatString(value);
  const [display, setDisplay] = useState(parsed ? 0 : value);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!parsed) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            const start = performance.now();
            const { target, hasCommas } = parsed;

            const step = (now) => {
              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              const current = Math.round(eased * target);
              setDisplay(formatNumber(current, hasCommas));
              if (progress < 1) {
                requestAnimationFrame(step);
              } else {
                setDisplay(formatNumber(target, hasCommas));
              }
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [parsed, duration]);

  if (!parsed) {
    return <span ref={ref}>{value}</span>;
  }

  return (
    <span ref={ref}>
      {parsed.prefix}
      {display}
      {parsed.suffix}
    </span>
  );
}

const stats = [
  {
    number: "17+",
    label: "Years of Continuous Operation",
    sub: "Since 2008 in Kamanga Compound, Lusaka",
  },
  {
    number: "14,000+",
    label: "Meals Served Per Year",
    sub: "And growing — goal is daily feeding",
  },
  {
    number: "27",
    label: "Houses Built",
    sub: "For widows and grandmothers raising orphaned grandchildren",
  },
  {
    number: "10",
    label: "Active Caregivers",
    sub: "Visiting people living with HIV/AIDS weekly",
  },
  {
    number: "7",
    label: "Active Programmes",
    sub: "Covering nutrition, education, care, and empowerment",
  },
  {
    number: "100s",
    label: "Children In School",
    sub: "Sponsored with fees, uniforms, and stationery",
  },
];

const testimonials = [
  {
    quote:
      "My three youngest children go to the Center to collect breakfast they bring home and share with the others. This has helped me have peace of mind knowing my children have eaten whilst I'm away at the market as I only come back in the afternoon.",
    name: "Naomi Banda",
    role: "Feeding Programme Beneficiary",
    programme: "Feeding Programme",
    image: "/images/profile.jpg",
  },
  {
    quote:
      "Thank you for educating my granddaughter, you send her to school, pay her school fees, buy her uniforms and books. Thank you very much.",
    name: "Ambuya Jacqueline",
    role: "School Sponsorship Beneficiary",
    programme: "School Sponsorship",
    image: "/images/profile.jpg",
  },
  {
    quote:
      "The Foundation built me a house. I no longer get worried when it rains. I am very happy and grateful to Food and Nutrition Foundation.",
    name: "Lianess",
    role: "Housing Beneficiary",
    programme: "Orphans & Vulnerable Children",
    image: "/images/profile.jpg",
  },
];

const impactStories = [
  {
    title: "A Meal That Frees a Mother",
    body: "When children collect breakfast from the Centre, they are not just receiving food — they are giving their mothers the freedom to work. Naomi Banda leaves for the market each morning knowing her children will not go hungry. That peace of mind enables her to earn, which means the family is more financially stable. One meal creates an economic chain reaction.",
    image: "/images/gallery/smiling-children-04.jpg",
  },
  {
    title: "A House That Changes Everything",
    body: "Before the Foundation built Lianess a home, every rainy season was a crisis. A leaking roof damaged belongings, endangered health, and kept children awake at night. With a safe home, the family can sleep, the children can study, and Lianess can focus on care rather than survival. 27 families now live in that security.",
    image: "/images/home-05.jpg",
  },
  {
    title: "An Education That Lasts Generations",
    body: "When the Foundation sponsors a girl's school fees, the ripple effect extends far beyond her graduation. Educated women earn more, invest more in their children's health and education, and are better positioned to break the cycle of poverty. Every scholarship is an investment in a generation.",
    image: "/images/gallery/children-05.jpg",
  },
];

export default function OurImpact() {
  return (
    <main>
      <section className="page-hero" aria-label="Our Impact page hero">
        <div
          className="page-hero-bg"
          style={{
            backgroundImage: "url('/images/gallery/children-03.jpg')",
          }}
        />
        <div className="container page-hero-content">
          <span className="eyebrow" style={{ color: "var(--amber-light)" }}>
            17+ Years of Service
          </span>
          <h1>The Numbers Behind the Hope</h1>
          <p>
            Every statistic here is a real person — a child fed, a home built, a
            mother educated, a caregiver supported.
          </p>
        </div>
      </section>

      {/* KEY STATS */}
      <section
        className="section stats-section"
        aria-labelledby="stats-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">Our Impact In Numbers</span>
            <h2 id="stats-heading">What 17+ Years of Commitment Looks Like</h2>
            <div className="divider divider-center" />
          </div>
          <div className="impact-stats-grid">
            {stats.map((s, i) => (
              <div className="impact-stat-card" key={i}>
                <div className="impact-stat-number">
                  <CountUp value={s.number} />
                </div>
                <div className="impact-stat-label">{s.label}</div>
                <div className="impact-stat-sub">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT STORIES */}
      <section
        className="section stories-section"
        aria-labelledby="stories-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">Real Stories</span>
            <h2 id="stories-heading">The Change We See Every Day</h2>
            <div className="divider divider-center" />
            <p>
              Impact is not just a number. It is a change in how a family lives,
              sleeps, learns, and hopes.
            </p>
          </div>
          <div className="stories-list">
            {impactStories.map((story, i) => (
              <div
                className={`story-row${i % 2 === 1 ? " reverse" : ""}`}
                key={i}
              >
                <div className="story-image">
                  <img src={story.image} alt={story.title} loading="lazy" />
                </div>
                <div className="story-content">
                  <h3>{story.title}</h3>
                  <p>{story.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        className="section testimonials-section"
        aria-labelledby="testimonials-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">From the Community</span>
            <h2 id="testimonials-heading">Their Words</h2>
            <div className="divider divider-center" />
          </div>
          <div className="testimonials-full-grid">
            {testimonials.map((t, i) => (
              <div className="testimonial-full" key={i}>
                <div className="testimonial-full-quote">"{t.quote}"</div>
                <div className="testimonial-full-footer">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="testimonial-avatar"
                  />
                  <div>
                    <p className="testimonial-name">{t.name}</p>
                    <p className="testimonial-prog">{t.programme}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DONATE CTA */}
      <section className="cta-band" aria-label="Donate CTA">
        <div className="container cta-band-inner">
          <div>
            <h2>Add Your Name to This Impact</h2>
            <p>Your donation today writes the next chapter of this story.</p>
          </div>
          <div className="cta-band-actions">
            <Link to="/donate" className="btn btn-primary btn-lg">
              Donate Now
            </Link>
            <Link to="/what-we-do" className="btn btn-secondary">
              See Our Programmes
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
